import { BrowserAnimationsModule } from '@angular/platform-browser/animations';
import { BrowserModule } from '@angular/platform-browser';
import { NgModule, ErrorHandler } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { RouterModule } from '@angular/router';
import { NgbModule } from '@ng-bootstrap/ng-bootstrap';
import { CommonModule } from '@angular/common';
import { AppRoutingModule } from './app.routing';
import { ComponentsModule } from './components/components.module';
import { ErrorsHandler } from './errors.handler';
import { ServerErrorsInterceptor } from './_interceptors/server-errors.interceptor';
import { JwtInterceptor } from './_interceptors/jwt.interceptor';
import { HttpCancelService } from './_services/http-cancel.service';
import { CancelHttpInterceptor } from './_interceptors/cancel-http.interceptor';
import { LatestOnlyHttpInterceptor } from './_interceptors/latest-only-http.interceptor';
import { HTTP_INTERCEPTORS, HttpClientModule } from '@angular/common/http';

// Services
import { NotificationService } from './_services/notification.service';
import { NotificationBadgeService } from './_services/notification-badge.service';
import { AuthService } from './_services/auth.service';
import { CryptoService } from './_services/crypto.service';
import { NetworkConnectionService } from './_services/monitoring/network-connection.service';

// Others
import { AppComponent } from './app.component';
import { AuthGuard } from './_guards/auth.guard';
import { TrainingAndInductionsGuard } from './_guards/training-and-inductions.guard'
import { Globals } from './_globals';
import { ObjectFilterPipe } from './_pipes/object-filter.pipe';

// Layouts
import { SupportLayoutComponent } from './layouts/support-layout/support-layout.component';
import { ExtLayoutComponent } from './layouts/ext-layout/ext-layout.component';
import { SiteOwnerLayoutComponent } from './layouts/site-owner-layout/site-owner-layout.component';
import { SiteDelegateLayoutComponent } from './layouts/site-delegate-layout/site-delegate-layout.component';
import { AuthorisingOfficerLayoutComponent } from './layouts/authorising-officer-layout/authorising-officer-layout.component';
import { WorkerLayoutComponent } from './layouts/worker-layout/worker-layout.component';
import { PowerStationOwnerLayoutComponent } from './layouts/power-station-owner/power-station-owner-layout.component';

// 3rd Party
import { CookieService } from 'ngx-cookie-service';
import { ToastrModule } from 'ngx-toastr';
import { OrderModule } from 'ngx-order-pipe';
import { LoadingBarHttpClientModule } from '@ngx-loading-bar/http-client';
import { LoadingBarModule } from '@ngx-loading-bar/core';
import { DragulaModule } from 'ng2-dragula';
import { AgmCoreModule } from '@agm/core';
import { GooglePlaceModule } from 'ngx-google-places-autocomplete';
import { AdminLayoutComponent } from './layouts/admin-layout/admin-layout.component';
import { SiteService } from './_services/site.service';
import { SupportUserService } from './_services/support-user.service';
import { SiteOwnerUserService } from './_services/site-owner-user.service';
import { ReportsService } from './_services/reports.service';
import { SiteTypesService } from './_services/site-types.service';
import { TrainingService } from './_services/training.service';
import { MessagesModule } from 'primeng/messages';
import { MessageModule } from 'primeng/message';
import { ContractorManagerService } from './_services/contractor-manager.service';
import { AssetTypesService } from './_services/asset-types.service';
import { SiteAssetsService } from './_services/site-assets.service';
import { SiteWorkersService } from './_services/site-workers.service';
import { ContractorService } from './_services/contractor.service';
import { SiteContactsService } from './_services/site-contacts.service';
import { SiteRestrictionsComponent } from './shared-pages/site-pages/site-restrictions/site-restrictions.component';
import { SiteIncidentsComponent } from './shared-pages/site-pages/site-incidents/site-incidents.component';
import { CompetencyTypesService } from './_services/competency-types.service';
import { SiteWorkOrdersService } from './_services/site-work-orders.service';
import { FileService } from './_services/file.service';
import { SiteWorkOrderProceduresService } from './_services/site-work-order-procedures.service';
import { SiteNotesService } from './_services/site-notes.service';
import { SiteIncidentsService } from './_services/site-incidents.service';
import { SiteDocumentsService } from './_services/site-documents.service';
import { LiveSessionService } from './_services/live-session.service';
import { SiteSessionsService } from './_services/site-sessions.service';
import { ContractorManagerLayoutComponent } from './layouts/contractor-manager-layout/contractor-manager-layout.component';
import { SessionsService } from './_services/sessions.service';
import { WorkOrderStateService } from './_services/work-order-state.service';
import { DashboardService } from './shared-pages/dashboard/dashboard.service';
import { UserRoleGuard } from './_guards/user-role.guard';
import { SiteFormsService } from './_services/site-forms.service';
import { PdfExportService } from './_services/pdf-export.service';
import { InviteUsersService } from './_services/invite-users.service';
import { CaptchaService } from './_services/captcha.service';
import { PdfExportCertificatesGeLevel1Service } from './_services/pdf-export-certificates/pdf-export-certificates-ge-level-1.service';
import { PdfExportCertificatesGeLevel2Service } from './_services/pdf-export-certificates/pdf-export-certificates-ge-level-2.service';
import { PdfExportCertificatesVentusLevel1Service } from './_services/pdf-export-certificates/pdf-export-certificates-ventus-level-1.service';
import { PdfExportCertificatesVentusLevel2Service } from './_services/pdf-export-certificates/pdf-export-certificates-ventus-level-2.service';
import { WorkflowItemService } from './_services/workflow-items.service';
import { ScheduleValidationService } from './_services/schedule-validation.service';
import { AssetAvailabilityScheduleService } from './_services/asset-availability-schedule.service';
import { WorkOrderRequiredCompetencyService } from './_services/work-order-required-competency.service';
import { ProcedureOemCompetencyService } from './_services/procedure-oem-competency.service';
import { SiteRequiredCompetencyService } from './_services/site-required-competency.service';
import { CompetencyService } from './_services/competency.service';
import { WorkerService } from './_services/worker.service';
import { ForgotPasswordService } from './_services/forgot-password.service';
import { TrainingCertService } from './_services/training-cert.service';
import { AdminCacheService } from './_services/admin-cache.service';
import { TrainingEnergiaOverrideService } from './_services/training-energia-override.service';
import { LoggerService } from './_services/logger.service';
import { PowerStationManagerLayoutComponent } from './layouts/power-station-manager/power-station-manager-layout.component';
import { RolePermissionService } from './_services/role-permission.service';
import { TabItemHelperService } from './_services/tab-item-helper.service';
import { WorkflowAccessService } from './_services/workflow-access.service';
import { PlausibleAnalyticsService } from './_services/plausible-analytics.service';
import { SiteFormValidationService } from './_services/site-form-validation-service';
import { SiteFormResponseFactory } from './_models/workflow-items/site-form-response.factory';

@NgModule({
  imports: [
    BrowserModule,
    BrowserAnimationsModule,
    FormsModule,
    CommonModule,
    HttpClientModule,
    ComponentsModule,
    RouterModule,
    AppRoutingModule,
    OrderModule,
    LoadingBarHttpClientModule,
    DragulaModule.forRoot(),
    LoadingBarModule,
    NgbModule,
    ToastrModule.forRoot(),
    AgmCoreModule.forRoot({
      apiKey: 'AIzaSyBNkfd6tHliSgiEdAvySEupucqJH_9nOPM',
      libraries: ['places'],
    }),

    GooglePlaceModule,
    MessagesModule,
    MessageModule,
  ],
  declarations: [
    AppComponent,
    SupportLayoutComponent,
    AdminLayoutComponent,
    SiteOwnerLayoutComponent,
    PowerStationOwnerLayoutComponent,
    PowerStationManagerLayoutComponent,
    SiteDelegateLayoutComponent,
    AuthorisingOfficerLayoutComponent,
    ContractorManagerLayoutComponent,
    WorkerLayoutComponent,
    ExtLayoutComponent,
    ObjectFilterPipe,
  ],
  providers: [
    RolePermissionService,
    NetworkConnectionService,
    NotificationService,
    NotificationBadgeService,
    AuthGuard,
    TrainingAndInductionsGuard,
    UserRoleGuard,
    Globals,
    AuthService,
    CookieService,
    CryptoService,
    SiteService,
    SiteIncidentsService,
    SupportUserService,
    SiteOwnerUserService,
    ReportsService,
    SiteTypesService,
    TrainingService,
    AdminCacheService,
    LoggerService,
    TrainingCertService,
    TrainingEnergiaOverrideService,
    CaptchaService,
    ContractorManagerService,
    AssetTypesService,
    SiteAssetsService,
    SiteWorkersService,
    ContractorService,
    SiteContactsService,
    SiteRestrictionsComponent,
    SiteIncidentsComponent,
    CompetencyTypesService,
    TabItemHelperService,
    WorkflowAccessService,
    SiteWorkOrdersService,
    FileService,
    SiteWorkOrderProceduresService,
    SiteNotesService,
    SiteDocumentsService,
    LiveSessionService,
    ObjectFilterPipe,
    SiteSessionsService,
    SessionsService,
    DashboardService,
    HttpCancelService,
    SiteFormsService,
    WorkerService,
    CompetencyService,
    PdfExportService,
    PdfExportCertificatesVentusLevel1Service,
    PdfExportCertificatesGeLevel1Service,
    PdfExportCertificatesVentusLevel2Service,
    PdfExportCertificatesGeLevel2Service,
    InviteUsersService,
    AssetAvailabilityScheduleService,
    ScheduleValidationService,
    WorkOrderRequiredCompetencyService,
    ProcedureOemCompetencyService,
    SiteRequiredCompetencyService,
    PlausibleAnalyticsService,
    SiteFormValidationService,
    SiteFormResponseFactory,
    {
      provide: HTTP_INTERCEPTORS,
      useClass: JwtInterceptor,
      multi: true,
    },
    {
      provide: HTTP_INTERCEPTORS,
      useClass: ServerErrorsInterceptor,
      multi: true,
    },
    {
      provide: HTTP_INTERCEPTORS,
      useClass: LatestOnlyHttpInterceptor,
      multi: true,
    },
    {
      provide: HTTP_INTERCEPTORS,
      useClass: CancelHttpInterceptor,
      multi: true,
    },
    {
      provide: ErrorHandler,
      useClass: ErrorsHandler,
    },
    WorkflowItemService,
    WorkOrderStateService,
    ForgotPasswordService,
  ],
  bootstrap: [AppComponent],
})
export class AppModule {}
